// @ts-nocheck — Browser-shipped asset that is not JSDoc-typed for the theme's
// checkJs (custom-element `refs` and union return types trip the editor's
// TypeScript checker without affecting runtime).
/**
 * Bundle Cart
 * -----------
 * Cart-side behaviour for theme-only bundles (see assets/bundle-product.js).
 *
 * Bundle lines are grouped by the private `_bundle_instance_id` line-item
 * property, exposed on each cart row as `data-bundle-instance-id` by
 * snippets/cart-products.liquid. This module wraps two public methods on the
 * theme's CartItemsComponent prototype:
 *
 * - `onLineItemRemove` — removing any line of a bundle (parent or component,
 *   via the remove button or a quantity of zero) removes every line that
 *   shares the same `_bundle_instance_id` in one cart/update.js request.
 * - `updateQuantity` — changing the quantity of a bundle line synchronizes
 *   the same quantity across the whole bundle instance, so an incomplete or
 *   lopsided bundle can never be created from the cart.
 *
 * Lines without a bundle instance id keep the theme's original behaviour
 * untouched. Two separate instances of the same bundle have different
 * instance ids and never affect each other, and a component product bought
 * independently has no instance id, so it is never touched.
 *
 * ---------------------------------------------------------------------------
 * THEME ADAPTATIONS versus the source (Shokuya) implementation
 * ---------------------------------------------------------------------------
 * 1. Cart events. `CartUpdateEvent` from `@theme/events` no longer exists in
 *    this theme; cart mutations are announced with Shopify Standard Events
 *    (`CartLinesUpdateEvent` from `@shopify/events`) carrying a deferred promise
 *    that resolves with the cart and the rendered `sections`. This mirrors
 *    assets/component-cart-items.js exactly, so every other cart-items-component
 *    on the page (drawer vs. cart page) refreshes itself from the same response.
 * 2. Endpoint. `Theme.routes.cart_update_url` (locale-aware) replaces the manual
 *    `window.Shopify.routes.root` concatenation, with the old form as a fallback.
 * 3. Empty-cart transition. This theme's `onLineItemRemove` also removes
 *    `#cart-drawer-heading` when the cart empties, so the optimistic bundle
 *    removal does the same.
 *
 * Unchanged from the source: the grouping algorithm, the single-request update
 * strategy, the per-instance in-flight lock, and the checkout submit guard.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { onAnimationEnd, prefersReducedMotion, resetShimmer, startViewTransition } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

const GENERIC_ERROR_MESSAGE = 'Unable to update the cart. Please try again.';

/** @type {Set<string>} Bundle instance ids with an in-flight cart request. */
const inFlightBundleUpdates = new Set();

/**
 * Collects everything needed to update one bundle instance, based on the
 * cart rows rendered by the theme. Returns null when the line is not part of
 * a bundle (or the data is malformed), so callers can fall back to the
 * theme's original behaviour.
 *
 * @param {HTMLElement} component - The cart-items-component element.
 * @param {number | string} line - The 1-based cart line number.
 * @returns {{instanceId: string, updateKeys: string[], memberRows: HTMLElement[], totalRows: number} | null}
 */
function getBundleGroup(component, line) {
  const rows = component.refs?.cartItemRows;
  const lineNumber = Number(line);

  if (!Array.isArray(rows) || !Number.isInteger(lineNumber) || lineNumber <= 0) return null;

  const row = rows[lineNumber - 1];
  if (!(row instanceof HTMLElement)) return null;

  const instanceId = row.dataset.bundleInstanceId;
  if (typeof instanceId !== 'string' || instanceId.trim() === '') return null;

  const groupRows = rows.filter(
    (groupRow) => groupRow instanceof HTMLElement && groupRow.dataset.bundleInstanceId === instanceId
  );

  const groupKeys = new Set();
  const updateKeys = new Set();

  for (const groupRow of groupRows) {
    const key = groupRow.dataset.key;
    if (!key) {
      console.warn(
        '[bundle-cart] A bundle cart row is missing its line item key. Falling back to the theme’s default behaviour.'
      );
      return null;
    }
    groupKeys.add(key);

    // Nested child lines (Shopify nested cart lines) cannot be updated
    // directly — Shopify cascades them from their parent line.
    if (!groupRow.dataset.parentKey) {
      updateKeys.add(key);
    }
  }

  if (updateKeys.size === 0) return null;

  // Rows to treat as part of this bundle in the UI: the matching lines plus
  // any nested child rows that hang off them (mirrors the theme's own
  // nested-line removal in onLineItemRemove).
  const memberRows = rows.filter(
    (memberRow) =>
      memberRow instanceof HTMLElement &&
      ((memberRow.dataset.key && groupKeys.has(memberRow.dataset.key)) ||
        (memberRow.dataset.parentKey && groupKeys.has(memberRow.dataset.parentKey)))
  );

  return {
    instanceId,
    updateKeys: Array.from(updateKeys),
    memberRows,
    totalRows: rows.length,
  };
}

/**
 * Section ids of every cart-items-component on the page, so the response can
 * re-render the drawer and the cart page together (mirrors the theme).
 * @param {HTMLElement} component
 * @returns {string}
 */
function collectCartSectionIds(component) {
  const ids = new Set();

  if (component.dataset.sectionId) ids.add(component.dataset.sectionId);

  for (const element of document.querySelectorAll('cart-items-component')) {
    if (element instanceof HTMLElement && element.dataset.sectionId) {
      ids.add(element.dataset.sectionId);
    }
  }

  return Array.from(ids).join(',');
}

/**
 * The locale-aware cart update endpoint.
 * @returns {string}
 */
function cartUpdateUrl() {
  const themeRoutes = typeof Theme !== 'undefined' ? Theme.routes : undefined;
  if (themeRoutes?.cart_update_url) return `${themeRoutes.cart_update_url}.js`;

  const root = window.Shopify?.routes?.root || '/';
  return `${root}cart/update.js`;
}

/**
 * Optimistically animates the bundle rows out of the cart UI, replicating
 * the theme's own onLineItemRemove animation and empty-cart handling.
 * @param {HTMLElement} component
 * @param {{memberRows: HTMLElement[], totalRows: number}} group
 */
function animateBundleRowsRemoval(component, group) {
  const isEmptyCart = group.memberRows.length === group.totalRows;

  const template = document.getElementById('empty-cart-template');
  if (isEmptyCart && template instanceof HTMLTemplateElement) {
    const clone = document.importNode(template.content, true);

    startViewTransition(() => {
      // Mirrors assets/component-cart-items.js: the drawer heading lives
      // outside the component and must go with the items.
      document.getElementById('cart-drawer-heading')?.remove();
      component.replaceChildren(clone);
    }, [component.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

    return;
  }

  for (const row of group.memberRows) {
    const remove = () => row.remove();

    if (prefersReducedMotion()) {
      remove();
      continue;
    }

    row.style.setProperty('--row-height', `${row.clientHeight}px`);
    row.classList.add('removing');
    onAnimationEnd(row, remove);
  }
}

/**
 * Shows the theme's per-line cart error for the interacted line.
 * @param {HTMLElement} component
 * @param {number} line
 * @param {string} message
 */
function showBundleLineError(component, line, message) {
  const host =
    (component.dataset.sectionId &&
      document.querySelector(`cart-items-component[data-section-id="${component.dataset.sectionId}"]`)) ||
    component;

  const errorText = host.querySelector(`[ref="cartItemError-${line}"]`);
  const errorContainer = host.querySelector(`[ref="cartItemErrorContainer-${line}"]`);

  if (errorText instanceof HTMLElement && errorContainer instanceof HTMLElement) {
    errorText.textContent = message;
    errorContainer.classList.remove('hidden');
  }
}

/**
 * Restores the cart UI after a failed bundle update by re-rendering the cart
 * sections from the server, then surfaces the error on the interacted line.
 * @param {HTMLElement} component
 * @param {number} line
 * @param {string} message
 */
async function restoreAfterFailure(component, line, message) {
  const sectionId = component.dataset.sectionId;

  if (sectionId) {
    try {
      await sectionRenderer.renderSection(sectionId, {
        cache: false,
        mode: component.isDrawer ? 'hydration' : 'full',
      });
    } catch (renderError) {
      console.error('[bundle-cart] Failed to re-render the cart after a bundle update error:', renderError);
    }
  }

  showBundleLineError(component, line, message);

  component.dispatchEvent(
    new CartErrorEvent({
      error: message,
      code: 'INVALID',
    })
  );
}

/**
 * Mirrors the theme's quantity-selector syncing after a cart response.
 * @param {{items?: Array<{variant_id: number, quantity: number}>}} cart
 */
function updateQuantitySelectorsFromCart(cart) {
  if (!Array.isArray(cart.items)) return;

  for (const item of cart.items) {
    const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${item.variant_id}"]`);

    for (const selector of selectors) {
      const input = selector.querySelector('input[data-cart-quantity]');
      if (!input) continue;

      input.setAttribute('data-cart-quantity', item.quantity.toString());

      if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
        selector.updateCartQuantity();
      }
    }
  }
}

/**
 * Mirrors the theme's button-state refresh after a cart section morph.
 * Called after morphSection because morphing preserves existing elements,
 * which can otherwise keep stale disabled states.
 */
function updateCartQuantityButtonStates() {
  for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
    /** @type {any} */ (selector).updateButtonStates?.();
  }
}

/**
 * Sends one cart/update.js request that sets every directly-updatable line of
 * the bundle instance to the same quantity (0 removes the whole bundle), then
 * refreshes the cart UI through the theme's existing section rendering and
 * Standard Events cart-lines-update handshake.
 *
 * @param {HTMLElement} component - The cart-items-component element.
 * @param {number} line - The 1-based line the customer interacted with.
 * @param {ReturnType<typeof getBundleGroup>} group - The bundle group.
 * @param {number} quantity - The target quantity for the whole bundle.
 * @param {string} action - 'clear' or 'change', for performance markers.
 */
async function submitBundleGroupUpdate(component, line, group, quantity, action) {
  if (!group || inFlightBundleUpdates.has(group.instanceId)) return;
  inFlightBundleUpdates.add(group.instanceId);

  const cartPerformanceMarker = cartPerformance.createStartingMarker(`${action}:user-action`);

  component.classList.add('cart-items-disabled');
  component.refs?.cartTotal?.shimmer?.();

  if (quantity === 0) {
    animateBundleRowsRemoval(component, group);
  } else {
    for (const row of group.memberRows) {
      /** @type {any} */ (row.querySelector('text-component'))?.shimmer?.();
    }
  }

  /** @type {Record<string, number>} */
  const updates = {};
  for (const key of group.updateKeys) {
    updates[key] = quantity;
  }

  const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
  let eventSettled = false;

  component.dispatchEvent(
    new CartLinesUpdateEvent({
      action: quantity > 0 ? 'update' : 'remove',
      context: 'cart',
      lines: group.updateKeys.map((key) => ({ id: key, quantity })),
      promise: deferredUpdatePromise.promise,
    })
  );

  try {
    const response = await fetch(cartUpdateUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        updates,
        sections: collectCartSectionIds(component),
        sections_url: window.location.pathname,
      }),
    });

    let cart = null;
    try {
      cart = await response.json();
    } catch (parseError) {
      cart = null;
    }

    if (!response.ok || !cart || cart.errors || cart.status) {
      const message = (cart && (cart.errors || cart.message || cart.description)) || GENERIC_ERROR_MESSAGE;
      const errorMessage = typeof message === 'string' ? message : GENERIC_ERROR_MESSAGE;

      console.error('[bundle-cart] Bundle cart update was rejected:', response.status, cart);

      eventSettled = true;
      deferredUpdatePromise.reject(new Error(errorMessage));

      await restoreAfterFailure(component, line, errorMessage);
      return;
    }

    resetShimmer(component);
    updateQuantitySelectorsFromCart(cart);

    eventSettled = true;
    deferredUpdatePromise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: cart.sections,
        items: cart.items,
        itemCount: Number(cart.item_count) || 0,
        source: 'cart-items-component',
        didError: false,
      },
    });

    const sectionId = component.dataset.sectionId;
    const ownSectionHtml = sectionId ? cart.sections?.[sectionId] : undefined;

    if (sectionId && ownSectionHtml) {
      await morphSection(sectionId, ownSectionHtml, { mode: component.isDrawer ? 'hydration' : 'full' });
      updateCartQuantityButtonStates();
    } else if (sectionId) {
      await sectionRenderer.renderSection(sectionId, {
        cache: false,
        mode: component.isDrawer ? 'hydration' : 'full',
      });
      updateCartQuantityButtonStates();
    }
  } catch (networkError) {
    console.error('[bundle-cart] Network error while updating a bundle in the cart:', networkError);

    if (!eventSettled) {
      eventSettled = true;
      deferredUpdatePromise.reject(networkError);
    }

    await restoreAfterFailure(component, line, GENERIC_ERROR_MESSAGE);
  } finally {
    // Never leave a listener awaiting a promise that will not settle.
    if (!eventSettled) deferredUpdatePromise.reject(new Error('Bundle cart update did not complete.'));

    inFlightBundleUpdates.delete(group.instanceId);
    component.classList.remove('cart-items-disabled');
    cartPerformance.measureFromMarker(cartPerformanceMarker);
  }
}

/**
 * The theme's checkout button natively submits the cart form
 * (`form="cart-form"`), which POSTs the positional `updates[]` quantity
 * inputs to /cart before redirecting to checkout. If the customer typed a
 * new quantity into one bundle line and clicked checkout before the Ajax
 * sync finished, that POST would apply the new quantity to a single line and
 * leave the bundle lopsided. This capture-phase guard equalizes the
 * `updates[]` values of every bundle instance just before the native submit,
 * so an inconsistent bundle can never be posted. Non-bundle inputs are never
 * touched.
 * @param {Event} event
 */
function normalizeBundleQuantitiesOnSubmit(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;

  /** @type {Map<string, HTMLInputElement[]>} */
  const groups = new Map();

  for (const row of form.querySelectorAll('[data-bundle-instance-id]')) {
    if (!(row instanceof HTMLElement)) continue;

    const instanceId = row.dataset.bundleInstanceId;
    if (!instanceId) continue;

    const input = row.querySelector('input[name="updates[]"]');
    if (!(input instanceof HTMLInputElement) || input.disabled) continue;

    const inputs = groups.get(instanceId) || [];
    inputs.push(input);
    groups.set(instanceId, inputs);
  }

  for (const inputs of groups.values()) {
    if (inputs.length < 2) continue;

    // An input whose value differs from its server-rendered defaultValue was
    // edited by the customer — apply that quantity to the whole bundle.
    const editedInput = inputs.find((input) => input.value !== input.defaultValue);
    const targetValue = editedInput ? editedInput.value : inputs[0].value;

    for (const input of inputs) {
      if (input.value !== targetValue) input.value = targetValue;
    }
  }
}

/**
 * Wraps the cart component's public removal and quantity methods. Lines that
 * belong to a bundle instance are handled as a group; every other line is
 * passed to the theme's original method untouched. Prototype patching covers
 * every instance (drawer and cart page, including section re-renders) without
 * adding any per-instance event listeners.
 */
function installBundleCartInterceptor() {
  const CartItemsComponent = customElements.get('cart-items-component');
  const prototype = CartItemsComponent?.prototype;

  if (!prototype) {
    console.error('[bundle-cart] cart-items-component is not defined. Bundle-aware cart removal is disabled.');
    return;
  }

  const originalOnLineItemRemove = prototype.onLineItemRemove;
  const originalUpdateQuantity = prototype.updateQuantity;

  if (typeof originalOnLineItemRemove !== 'function' || typeof originalUpdateQuantity !== 'function') {
    console.error('[bundle-cart] Expected cart methods are missing. Bundle-aware cart removal is disabled.');
    return;
  }

  if (originalOnLineItemRemove.bundleCartPatched || originalUpdateQuantity.bundleCartPatched) return;

  /**
   * @this {HTMLElement}
   * @param {number} line
   */
  function onLineItemRemoveWithBundles(line, ...rest) {
    const group = getBundleGroup(this, line);
    if (!group) {
      originalOnLineItemRemove.call(this, line, ...rest);
      return;
    }

    submitBundleGroupUpdate(this, Number(line), group, 0, 'clear');
  }

  /**
   * @this {HTMLElement}
   * @param {{line: number, quantity: number, action: string}} config
   */
  function updateQuantityWithBundles(config, ...rest) {
    const line = config && typeof config === 'object' ? config.line : undefined;
    const group = line ? getBundleGroup(this, line) : null;

    if (!group) {
      originalUpdateQuantity.call(this, config, ...rest);
      return;
    }

    const requestedQuantity = Number(config.quantity);
    const quantity = Number.isFinite(requestedQuantity) && requestedQuantity > 0 ? Math.floor(requestedQuantity) : 0;

    submitBundleGroupUpdate(this, Number(line), group, quantity, quantity === 0 ? 'clear' : 'change');
  }

  onLineItemRemoveWithBundles.bundleCartPatched = true;
  updateQuantityWithBundles.bundleCartPatched = true;

  prototype.onLineItemRemove = onLineItemRemoveWithBundles;
  prototype.updateQuantity = updateQuantityWithBundles;
}

document.addEventListener('submit', normalizeBundleQuantitiesOnSubmit, { capture: true });

customElements.whenDefined('cart-items-component').then(installBundleCartInterceptor);
